import RenderEntity, { RENDER_ENTITY_FORMAT3 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// lazy loaded components
const ClientSelect = lazy(() => import('ui-component/fields/EntitySelect/crm/ClientSelect'));
const ContractTypeSelect = lazy(() => import('ui-component/fields/EntitySelect/crm/ContractTypeSelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => [
                ...appGridActions(capabilities, params?.row, permissionData, onClickActions)
            ]
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'code',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityhasautogencode-code'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'client',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="client"
                    renderPattern="<name>"
                    renderTooltipPattern="[<code>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-crm-client'
        },
        {
            field: 'contractType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="contractType"
                    renderPattern="<name>"
                    renderTooltipPattern="[<acronym>] <name>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-crm-contracttype'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                code: true,
                name: true,
                description: false,
                client: true,
                contractType: true,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'code',
                'name',
                'description',
                'client',
                'contractType',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                code: { width: 100 },
                name: { width: 150 },
                description: { width: 100 },
                client: { width: 100 },
                contractType: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'code', fieldType: 'simple' },
            label: 'traits-appentityhasautogencode-code'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'clientId', fieldType: 'simple' },
            label: 'entity-crm-client',
            renderField: (params) => <ClientSelect key="filterContract-client" multiple {...params} />
        },
        {
            filterType: 'reference',
            field: { fieldName: 'contractTypeId', fieldType: 'simple' },
            label: 'entity-crm-contracttype',
            renderField: (params) => <ContractTypeSelect key="filterContract-contractType" multiple {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterContract-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterContract-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterContract-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-contract',
        fields: [
            { fieldName: 'code', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'clientCode', fieldType: 'simple' },
            { fieldName: 'clientName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'code', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'clientId', fieldType: 'simple' },
        { fieldName: 'contractTypeId', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
